import React from 'react';
import { datetime, DatetimeType } from '@common/services/datetime';
import { FormInstance } from 'antd';
import { ShiftTypesRadioInput } from '@jobs/components';
import {
  DatePicker,
  LocalDate,
  InputNumber,
  Form,
  Col,
  Row,
  Input,
} from '@common/components';
import { getLengthInWeeksValue } from 'works/legacy/services/length-in-weeks-service';
import {
  ExtensionOfferAssignmentType,
  ExtensionOfferApplicationType,
  ExtensionOfferType,
} from '@working-experience/graphql/queries/extension-offer-query';

export type ExtensionOfferChangeResponseFormValuesType = {
  endDate: DatetimeType;
  timeoffDescription: string;
  shiftType: string;
  shiftsPerWeek: number;
  hoursPerShift: number;
  offerChangeResponseMessage?: string;
  extensionLength?: number;
};

type ExtensionOfferChangeResponseFormPropsType = {
  form: FormInstance<ExtensionOfferChangeResponseFormValuesType>;
  onFinish?: (values: ExtensionOfferChangeResponseFormValuesType) => void;
  onChange?: () => void;
  assignment: ExtensionOfferAssignmentType;
  application: ExtensionOfferApplicationType;
  extensionOffer: ExtensionOfferType;
  initialValues?: Partial<ExtensionOfferChangeResponseFormValuesType>;
};

export const endDateForWeeks = (
  finalEndDate: string | null | undefined,
  weeks: number
): DatetimeType => datetime(finalEndDate, 'YYYY-MM-DD').add(weeks, 'weeks');

export const isBeforeFinalEndOfDay = (
  current: DatetimeType,
  finalEndDate: string | null | undefined
): boolean => current < datetime(finalEndDate).endOf('day');

export function ExtensionOfferChangeResponseForm({
  form,
  onFinish,
  onChange = () => {},
  assignment,
  application,
  extensionOffer,
  initialValues = {},
}: ExtensionOfferChangeResponseFormPropsType) {
  const staffingRequest = application.staffingRequest;
  const isOnDemand = staffingRequest.staffingRequestType === 'on_demand';
  const defaultExtensionLength = getLengthInWeeksValue(
    assignment.finalEndDate,
    datetime(extensionOffer.endDate)
  );

  const onLengthChange = (event: React.FocusEvent<HTMLInputElement>) => {
    const weeks = parseInt(event.target.value, 10);
    if (weeks) {
      // NOTE: antd's DatePicker keeps the stale value unless the field is reset first.
      form.resetFields(['endDate']);

      form.setFieldsValue({
        endDate: endDateForWeeks(assignment.finalEndDate, weeks),
      });
    }

    onChange();
  };

  const onEndDateChange = (value: DatetimeType | null) => {
    if (value) {
      const baseDate = datetime(assignment.finalEndDate, 'YYYY-MM-DD');
      form.setFieldsValue({
        extensionLength: getLengthInWeeksValue(baseDate, value),
      });
    }

    onChange();
  };

  return (
    <div className="extension-offer-change-response-form">
      <Form
        layout="vertical"
        form={form}
        onFinish={onFinish}
        onChange={onChange}
        initialValues={{
          ...initialValues,
          extensionLength: defaultExtensionLength,
        }}
      >
        <Row gutter={[8, 8]}>
          <Col span={12}>
            <Form.Item label="Extension Length (in weeks)" required>
              <Form.Item
                name="extensionLength"
                noStyle
                rules={[
                  {
                    required: true,
                    message: 'Please enter an extension length',
                  },
                ]}
              >
                <InputNumber
                  data-testid="extension-length-input"
                  size="large"
                  onBlur={onLengthChange}
                />
              </Form.Item>
              <div className="hint">
                Current end date:{' '}
                <strong>
                  <LocalDate date={assignment.finalEndDate || ''} />
                </strong>
              </div>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Extension End Date"
              name="endDate"
              rules={[{ required: true, message: 'Please enter an end date' }]}
            >
              <DatePicker
                data-testid="ext-end-date"
                autoComplete="off"
                name="endDate"
                size="large"
                placeholder="mm/dd/yyyy"
                format="MM/DD/YYYY"
                showToday={false}
                defaultValue={datetime(extensionOffer.endDate)}
                defaultPickerValue={datetime(extensionOffer.endDate)}
                disabledDate={(current) =>
                  current &&
                  isBeforeFinalEndOfDay(current, assignment.finalEndDate)
                }
                onChange={onEndDateChange}
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          label="Time Off"
          name="timeoffDescription"
          rules={[{ required: true, message: 'Please enter a time off' }]}
        >
          <Input />
        </Form.Item>

        <ShiftTypesRadioInput />

        <Row gutter={[8, 8]}>
          <Col span={12}>
            <Form.Item
              label="Shifts per week"
              name="shiftsPerWeek"
              rules={[
                {
                  required: true,
                  message: 'Please enter the amount of shifts per week',
                },
                {
                  type: 'number',
                  min: isOnDemand ? 0 : 1,
                  max: 999,
                  message: 'Shifts per week must be positive',
                },
              ]}
            >
              <InputNumber onChange={onChange} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Hours per shift"
              name="hoursPerShift"
              rules={[
                {
                  required: true,
                  message: 'Please enter the amount of hours per shift',
                },
                {
                  type: 'number',
                  min: isOnDemand ? 0 : 1,
                  max: 999,
                  message: 'Hours per shift must be positive',
                },
              ]}
            >
              <InputNumber onChange={onChange} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <style jsx>
        {`
          .extension-offer-change-response-form {
            margin-top: 1em;
          }
          .extension-offer-change-response-form :global(.ant-input-number),
          .extension-offer-change-response-form
            :global(.ant-form-item-control-input-content),
          .extension-offer-change-response-form :global(.ant-picker) {
            width: 100%;
          }
          .extension-offer-change-response-form :global(.link-icon) {
            margin-left: 8px;
            font-size: 11px;
          }
          .extension-offer-change-response-form :global(.help-link:hover) {
            text-decoration: underline;
          }
        `}
      </style>
    </div>
  );
}
